import PauseIcon from "@mui/icons-material/Pause";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import {
  SLA_ACTIVITIES_TEXT_KEYS,
  slaActivityWorkingDuration,
} from "model/slaActivitiesModel";
import { SLA_MONITOR_TEXT_KEYS } from "model/slaMonitorModel";
import {
  SLA_HEALTH_AT_RISK,
  SLA_HEALTH_BREACHED,
  SLA_HEALTH_ON_TIME,
} from "model/slaModel";
import type { Task } from "model/activitiesModel";
import React from "react";
import { useSlaActivities, useSlaActivityForTask } from "./slaActivitiesContext";
import SlaInstanceSummaryLine, {
  slaProcessIsWorse,
  useSlaInstanceSummary,
} from "./SlaInstanceSummaryLine";

/**
 * **Badge SLA sulla riga ToDo** (criterio F3c: "SLA attivo **gia' in ToDo**").
 *
 * Vive dentro `TaskCard` (sez. 3 riga "Attivita'": "src/pages/MyActivities.tsx,
 * componenti task e dettaglio") e legge la vista dal contesto della superficie:
 * nessuna chiamata per riga (sez. 3: "nessun N+1"). Senza SLA — o fuori dalla
 * pagina attivita' — non rende nulla, quindi non altera le altre liste che usano
 * `TaskCard`.
 *
 * Regole rispettate:
 * - **stato anche in forma, non solo colore** (sez. 6 riga 91, sez. 5 F3c): il
 *   badge porta il testo dello stato (chiave del manifest), un'icona
 *   (`data-health-icon`) e l'attributo `data-health`;
 * - **countdown nella base lavorativa** (sez. 5 F3c): i secondi residui e di
 *   ritardo sono quelli del payload, esposti come numeri
 *   (`data-working-seconds`, `data-overdue-seconds`) oltre che formattati con le
 *   chiavi di durata esistenti;
 * - **niente azioni solo su hover**: il badge e' informativo e l'apertura del
 *   dettaglio resta il comando esistente della card (azione reale);
 * - **il countdown non annuncia ogni secondo** allo screen reader (sez. 6 riga
 *   91): il contenitore dichiara `aria-live="off"`.
 */
interface SlaTaskSlaBadgeProps {
  task: Task;
  /**
   * `full` (default): stato, countdown, scadenza ed escalation in riga.
   * `compact`: un solo chip colorato per stato ("In orario · 4m") con i dettagli
   * nel tooltip; pensato per le card ad altezza fissa (`TaskCard`), dove la
   * versione estesa spingeva fuori dalla card il resto del contenuto.
   */
  variant?: "full" | "compact";
}

/**
 * Durata breve localizzata ("4m", "2h", "30s") con le unita' `narrow` di
 * `Intl.NumberFormat`: nessuna chiave di traduzione nuova. Stesse soglie di
 * `slaMonitorWorkingDuration`.
 */
const shortWorkingDuration = (
  seconds: number | null | undefined,
  locale: string,
): string | null => {
  if (seconds === null || seconds === undefined || seconds < 0) return null;
  const [amount, unit, digits] =
    seconds >= 3600
      ? [seconds / 3600, "hour", 1]
      : seconds >= 60
        ? [Math.round(seconds / 60), "minute", 0]
        : [seconds, "second", 0];
  try {
    return new Intl.NumberFormat(locale, {
      style: "unit",
      unit: unit as string,
      unitDisplay: "narrow",
      maximumFractionDigits: digits as number,
    }).format(amount as number);
  } catch {
    return `${amount}${(unit as string).charAt(0)}`;
  }
};

/** Testo presente nel DOM (screen reader, test) ma non a video. */
const VISUALLY_HIDDEN: React.CSSProperties = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: 1,
  margin: -1,
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: 1,
};

export const healthChipColor = (
  health: number | null,
): "success" | "warning" | "error" | "default" => {
  if (health === SLA_HEALTH_ON_TIME) return "success";
  if (health === SLA_HEALTH_AT_RISK) return "warning";
  if (health === SLA_HEALTH_BREACHED) return "error";
  return "default";
};

const SlaTaskSlaBadge: React.FC<SlaTaskSlaBadgeProps> = ({
  task,
  variant = "full",
}) => {
  const translations = useTranslations();
  const context = useSlaActivities();
  const view = useSlaActivityForTask(task);
  // Con uno SLA di attivita' e uno di processo vivi insieme, il chip resta
  // quello di attivita' e il processo compare nel tooltip / come riga secondaria.
  const activitySummary = useSlaInstanceSummary(view, "activity");
  const processSummary = useSlaInstanceSummary(view?.process ?? null, "process");

  if (!view) return null;

  const { instance, detail, countdown, process } = view;
  const processWorse = slaProcessIsWorse(view, process);
  const prefix = `sla-task-${task.idprocesstoken}`;
  const locale = context?.locale || "en";
  const remaining = slaActivityWorkingDuration(countdown.remainingSeconds, locale);
  const overdue = slaActivityWorkingDuration(countdown.overdueSeconds, locale);

  /**
   * Stato in chiaro. Quando il payload non porta uno dei tre valori del contratto
   * (`healthKey === null`) la riga dichiara che lo SLA c'e' ma lo stato non e'
   * reso: nessun quarto stato inventato e nessun verde implicito (sez. 11.1).
   */
  const healthLabel = detail.healthKey
    ? getTranslation(translations, detail.healthKey)
    : getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.todoLabel);

  const remainingText = remaining
    ? `${remaining.value} ${getTranslation(translations, remaining.unitKey)}`
    : getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.countdownNotAvailable);
  const overdueText =
    overdue && overdue.value !== "0"
      ? ` (+${overdue.value} ${getTranslation(translations, overdue.unitKey)})`
      : "";
  const deadlineText = `${getTranslation(
    translations,
    SLA_ACTIVITIES_TEXT_KEYS.bannerDeadline,
  )}: ${detail.effectiveLabel}`;
  const escalationText = `${getTranslation(
    translations,
    SLA_MONITOR_TEXT_KEYS.escalationLabel,
  )}: ${
    detail.escalationValue ||
    getTranslation(translations, SLA_MONITOR_TEXT_KEYS.escalationNone)
  }`;

  if (variant === "compact") {
    const hasOverdue = (countdown.overdueSeconds ?? 0) > 0;
    const short = hasOverdue
      ? `+${shortWorkingDuration(countdown.overdueSeconds, locale)}`
      : shortWorkingDuration(countdown.remainingSeconds, locale);
    const tooltip = process ? (
      <Stack spacing={0.25}>
        <strong>{activitySummary}</strong>
        {countdown.paused && (
          <span>
            {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.countdownPaused)}
          </span>
        )}
        <span>{escalationText}</span>
        {detail.overridden && (
          <span>{getTranslation(translations, SLA_MONITOR_TEXT_KEYS.overridden)}</span>
        )}
        <Box component="span" sx={{ pt: 0.5, fontWeight: processWorse ? 700 : 400 }}>
          {processSummary}
        </Box>
      </Stack>
    ) : (
      <Stack spacing={0.25}>
        <span>{healthLabel}</span>
        <span>
          {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.countdownLabel)}
          {": "}
          {remainingText}
          {overdueText}
        </span>
        {countdown.paused && (
          <span>
            {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.countdownPaused)}
          </span>
        )}
        <span>{deadlineText}</span>
        <span>{escalationText}</span>
        {detail.overridden && (
          <span>{getTranslation(translations, SLA_MONITOR_TEXT_KEYS.overridden)}</span>
        )}
      </Stack>
    );
    const processDotColor = process ? healthChipColor(process.detail.health) : "default";

    return (
      <Box
        component="span"
        data-testid={prefix}
        data-instance-id={instance.id}
        data-health={detail.health === null ? "" : detail.health}
        data-overridden={detail.overridden}
        data-process-health={process?.detail.health ?? ""}
        data-process-worse={processWorse}
        sx={{
          display: "inline-flex",
          position: "relative",
          minWidth: 0,
          maxWidth: "100%",
          flexShrink: 1,
        }}
      >
        <Tooltip title={tooltip} arrow placement="top">
          <Chip
            size="small"
            color={healthChipColor(detail.health)}
            variant={detail.health === SLA_HEALTH_ON_TIME ? "outlined" : "filled"}
            data-testid={`${prefix}-health`}
            data-health={detail.health === null ? "" : detail.health}
            data-health-icon={detail.health === null ? "" : detail.health}
            icon={
              countdown.paused ? (
                <PauseIcon
                  data-testid={`${prefix}-paused`}
                  data-next-clock-change-at={countdown.nextClockChangeAt ?? ""}
                  aria-label={getTranslation(
                    translations,
                    SLA_ACTIVITIES_TEXT_KEYS.countdownPaused,
                  )}
                />
              ) : undefined
            }
            label={
              <>
                {healthLabel}
                {short ? " · " : ""}
                <span
                  data-testid={`${prefix}-countdown`}
                  data-working-seconds={countdown.remainingSeconds ?? ""}
                  data-overdue-seconds={countdown.overdueSeconds ?? ""}
                  data-clock-running={String(countdown.running)}
                  aria-live="off"
                >
                  {short ?? ""}
                </span>
                {/* Dettagli per screen reader e test: il tooltip non e' nel DOM
                    finche' non si apre. */}
                <span
                  style={VISUALLY_HIDDEN}
                  data-testid={`${prefix}-deadline`}
                  data-timezone={detail.timezoneId}
                >
                  {deadlineText}
                </span>
                <span
                  style={VISUALLY_HIDDEN}
                  data-testid={`${prefix}-escalation`}
                >
                  {escalationText}
                </span>
                {detail.overridden && (
                  <span
                    style={VISUALLY_HIDDEN}
                    data-testid={`${prefix}-overridden`}
                  >
                    {getTranslation(translations, SLA_MONITOR_TEXT_KEYS.overridden)}
                  </span>
                )}
                {process && (
                  <span
                    style={VISUALLY_HIDDEN}
                    data-testid={`${prefix}-process`}
                    data-instance-id={process.instance.id}
                  >
                    {processSummary}
                  </span>
                )}
              </>
            }
            sx={{
              height: 20,
              maxWidth: "100%",
              fontSize: 11,
              fontWeight: 600,
              borderRadius: "6px",
              "& .MuiChip-label": { px: 0.75 },
              "& .MuiChip-icon": { fontSize: 14, ml: 0.5, mr: -0.25 },
            }}
          />
        </Tooltip>
        {/* Processo messo peggio dell'attivita': puntino discreto sul chip, cosi'
            si nota senza hover (il dettaglio e' nel tooltip). */}
        {processWorse && processDotColor !== "default" && (
          <Box
            component="span"
            aria-hidden
            data-testid={`${prefix}-process-dot`}
            sx={{
              position: "absolute",
              top: -3,
              right: -3,
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: `${processDotColor}.main`,
              border: "1.5px solid",
              borderColor: "background.paper",
              pointerEvents: "none",
            }}
          />
        )}
      </Box>
    );
  }

  return (
    <Stack
      data-testid={prefix}
      data-instance-id={instance.id}
      data-health={detail.health === null ? "" : detail.health}
      data-overridden={detail.overridden}
      direction="row"
      spacing={0.5}
      alignItems="center"
      flexWrap="wrap"
      sx={{ rowGap: 0.5 }}
    >
      <Chip
        size="small"
        label={healthLabel}
        data-testid={`${prefix}-health`}
        data-health={detail.health === null ? "" : detail.health}
        data-health-icon={detail.health === null ? "" : detail.health}
      />
      <Typography
        variant="caption"
        data-testid={`${prefix}-countdown`}
        data-working-seconds={countdown.remainingSeconds ?? ""}
        data-overdue-seconds={countdown.overdueSeconds ?? ""}
        data-clock-running={String(countdown.running)}
        aria-live="off"
      >
        {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.countdownLabel)}
        {": "}
        {remaining
          ? `${remaining.value} ${getTranslation(translations, remaining.unitKey)}`
          : getTranslation(
              translations,
              SLA_ACTIVITIES_TEXT_KEYS.countdownNotAvailable,
            )}
        {overdue && overdue.value !== "0"
          ? ` (+${overdue.value} ${getTranslation(translations, overdue.unitKey)})`
          : ""}
      </Typography>
      {/* Pausa: lo stato e' anche testo, con l'istante di cambio clock dichiarato. */}
      {countdown.paused && (
        <Typography
          variant="caption"
          data-testid={`${prefix}-paused`}
          data-next-clock-change-at={countdown.nextClockChangeAt ?? ""}
        >
          {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.countdownPaused)}
        </Typography>
      )}
      {/* Scadenza vigente nella timezone dichiarata dalla riga (sez. 4). */}
      <Typography
        variant="caption"
        data-testid={`${prefix}-deadline`}
        data-timezone={detail.timezoneId}
      >
        {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.bannerDeadline)}
        {": "}
        {detail.effectiveLabel}
      </Typography>
      {/* "livello n/m" accanto allo stato, mai al posto dello stato (sez. 14.1). */}
      <Typography variant="caption" data-testid={`${prefix}-escalation`}>
        {getTranslation(translations, SLA_MONITOR_TEXT_KEYS.escalationLabel)}
        {": "}
        {detail.escalationValue ||
          getTranslation(translations, SLA_MONITOR_TEXT_KEYS.escalationNone)}
      </Typography>
      {/* Vigente prorogata da un override: l'originale non e' stata riscritta. */}
      {detail.overridden && (
        <Chip
          size="small"
          variant="outlined"
          label={getTranslation(translations, SLA_MONITOR_TEXT_KEYS.overridden)}
          data-testid={`${prefix}-overridden`}
        />
      )}
      {/* SLA del processo come riga secondaria quando governa uno SLA di attivita'. */}
      <SlaInstanceSummaryLine
        view={process}
        target="process"
        testId={`${prefix}-process`}
        sx={{ flexBasis: "100%" }}
      />
    </Stack>
  );
};

export default SlaTaskSlaBadge;
